import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Container,
  Badge,
  Alert,
  Button,
} from "react-bootstrap";
import {
  Copy,
  Key,
  User,
  Link as LinkIcon,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Eye,
  EyeOff,
  Info,
} from "react-feather";
import Swal from "sweetalert2";

import UtiPanCardService from "../../../services/UtiPanCardService";
import SideBar from "../../../components/Sidebar";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";

const ViewCredentials = () => {
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchAgentCredentials();
  }, []);

  const fetchAgentCredentials = async () => {
    if (loading) return;

    setLoading(true);

    try {
      const result = await UtiPanCardService.agentDetails();

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        setResponseData(res.data);
      } else {
        Swal.fire("Oops!", res.message || "Something went wrong", "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        Swal.fire({
          icon: "success",
          title: "Copied!",
          text: `${label} copied to clipboard`,
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
        Swal.fire("Error", "Failed to copy text", "error");
      });
  };

  const getStatusBadge = (status) => {
    switch (status?.toUpperCase()) {
      case "ACTIVE":
        return (
          <Badge bg="success" className="px-3 py-2">
            <CheckCircle size={14} /> Active
          </Badge>
        );
      case "INACTIVE":
        return (
          <Badge bg="danger" className="px-3 py-2">
            <AlertCircle size={14} /> Inactive
          </Badge>
        );
      case "PENDING":
        return (
          <Badge bg="warning" className="px-3 py-2">
            Pending
          </Badge>
        );
      default:
        return (
          <Badge bg="secondary" className="px-3 py-2">
            {status || "Unknown"}
          </Badge>
        );
    }
  };

  const loginUrl =
    "https://www.psaonline.utiitsl.com/psapanservices/forms/login.html/loginHome";

  return (
    <Container fluid className="mt-3">
      {loading && <LoadingScreen />}

      <Row>
        <SideBar />

        <Col md={9}>
          <Card className="shadow-sm p-4 mb-4">
            <div className="d-flex align-items-center mb-4">
              <div className="bg-primary p-2 rounded-circle me-3">
                <Key size={24} color="white" />
              </div>
              <div>
                <h4 className="mb-0">UTI PAN Agent Credentials</h4>
                <p className="text-muted mb-0">
                  Your login details for UTI PAN services
                </p>
              </div>
            </div>

            {responseData ? (
              <>
                <Alert
                  variant="info"
                  className="d-flex align-items-center mb-4"
                >
                  <Info size={20} className="me-2" />
                  <div className="small">
                    <strong>Important:</strong> Keep your credentials secure and
                    do not share them with anyone. Change your password
                    immediately after first login.
                  </div>
                </Alert>

                {/* Credentials in a single, clean layout */}
                <div className="border rounded p-4 mb-4 bg-light">
                  <Row className="g-4">
                    {/* UTI PSA ID */}
                    <Col md={6}>
                      <div className="mb-3">
                        <div className="d-flex align-items-center mb-2">
                          <User size={18} className="text-primary me-2" />
                          <label className="form-label fw-semibold mb-0">
                            UTI PSA ID
                          </label>
                        </div>
                        <div className="d-flex align-items-center">
                          <div className="bg-white p-3 rounded border flex-grow-1">
                            <code className="fs-6">
                              {responseData.agent_id}
                            </code>
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="ms-2"
                            onClick={() =>
                              copyToClipboard(
                                responseData.agent_id,
                                "UTI PSA ID"
                              )
                            }
                          >
                            <Copy size={14} />
                          </Button>
                        </div>
                      </div>
                    </Col>

                    {/* Default Password */}
                    <Col md={6}>
                      <div className="mb-3">
                        <div className="d-flex align-items-center mb-2">
                          <Key size={18} className="text-primary me-2" />
                          <label className="form-label fw-semibold mb-0">
                            Default Password
                          </label>
                        </div>
                        <div className="d-flex align-items-center">
                          <div className="bg-white p-3 rounded border flex-grow-1">
                            <code className="fs-6">
                              {showPassword
                                ? responseData.password
                                : "•".repeat(
                                    responseData.password?.length || 8
                                  )}
                            </code>
                          </div>
                          <div className="ms-2 d-flex">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="me-1"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <EyeOff size={14} />
                              ) : (
                                <Eye size={14} />
                              )}
                            </Button>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() =>
                                copyToClipboard(
                                  responseData.password,
                                  "Password"
                                )
                              }
                            >
                              <Copy size={14} />
                            </Button>
                          </div>
                        </div>
                        <small className="text-muted mt-2 d-block">
                          This is your default password. Please change it after
                          first login.
                        </small>
                      </div>
                    </Col>

                    {/* Status */}
                    <Col md={6}>
                      <div className="mb-3">
                        <div className="d-flex align-items-center mb-2">
                          <CheckCircle
                            size={18}
                            className="text-primary me-2"
                          />
                          <label className="form-label fw-semibold mb-0">
                            Account Status
                          </label>
                        </div>
                        <div className="bg-white p-3 rounded border">
                          {getStatusBadge(responseData.status)}
                        </div>
                      </div>
                    </Col>

                    {/* Login URL */}
                    <Col md={6}>
                      <div className="mb-3">
                        <div className="d-flex align-items-center mb-2">
                          <LinkIcon size={18} className="text-primary me-2" />
                          <label className="form-label fw-semibold mb-0">
                            Login URL
                          </label>
                        </div>
                        <div className="d-flex align-items-center">
                          <div className="bg-white p-3 rounded border flex-grow-1">
                            <small className="text-break">{loginUrl}</small>
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="ms-2"
                            onClick={() =>
                              copyToClipboard(loginUrl, "Login URL")
                            }
                          >
                            <Copy size={14} />
                          </Button>
                        </div>
                        <Button
                          variant="primary"
                          href={loginUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-100 mt-3"
                        >
                          <ExternalLink size={14} className="me-2" />
                          Go to Login Page
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Quick Login Instructions - Remains same */}
                <Card className="border mt-4">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">Quick Login Instructions</h6>
                  </Card.Header>
                  <Card.Body>
                    <ol className="mb-0">
                      <li>
                        Click on "Go to Login Page" or visit the login URL
                      </li>
                      <li>
                        Enter your <strong>UTI PSA ID</strong> and{" "}
                        <strong>Default Password</strong>
                      </li>
                      <li>
                        Complete your first login and change your password
                        immediately
                      </li>
                      <li>
                        For security reasons, do not share your credentials with
                        anyone
                      </li>
                    </ol>
                  </Card.Body>
                </Card>
              </>
            ) : (
              !loading && (
                <div className="text-center py-5">
                  <AlertCircle size={48} className="text-muted mb-3" />
                  <h5>Agent Not Onboarded | No credentials found</h5>
                  <p className="text-muted">
                    It seems this agent is not onboarded yet. For assistance,
                    please contact our support team.
                  </p>
                  <Button variant="primary" onClick={fetchAgentCredentials}>
                    Refresh
                  </Button>
                </div>
              )
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ViewCredentials;
